/**
 * WEB-11 provisional buyback estimate (pure). Deliberately simple and
 * explainable: base value by segment and age, then condition multipliers.
 * The amount is NON-BINDING; the final offer follows a physical inspection (PRD C-08).
 */

export const SEGMENTS = [
  { value: 'apple', label: 'Apple MacBook', base_paise: 6000000 },
  { value: 'business', label: 'Business (Latitude, EliteBook, ThinkPad)', base_paise: 4000000 },
  { value: 'gaming', label: 'Gaming (TUF, Legion, Victus, ROG)', base_paise: 5000000 },
  { value: 'consumer', label: 'Everyday (Inspiron, Pavilion, IdeaPad, VivoBook)', base_paise: 3000000 },
];

export const AGES = [
  { value: '1', label: 'Under 1 year', factor: 0.75 },
  { value: '2', label: '1 – 2 years', factor: 0.6 },
  { value: '3', label: '2 – 3 years', factor: 0.45 },
  { value: '4', label: '3 – 5 years', factor: 0.3 },
  { value: '5', label: 'Over 5 years', factor: 0.15 },
];

export const QUESTIONS = [
  { key: 'powers_on', label: 'Does it switch on and boot?', options: [{ value: 'yes', label: 'Yes', factor: 1 }, { value: 'no', label: 'No', factor: 0.3 }] },
  { key: 'screen', label: 'Screen condition', options: [{ value: 'perfect', label: 'Flawless', factor: 1 }, { value: 'minor', label: 'Minor scratches', factor: 0.92 }, { value: 'damaged', label: 'Cracked, lines or dead pixels', factor: 0.65 }] },
  { key: 'body', label: 'Body condition', options: [{ value: 'perfect', label: 'Like new', factor: 1 }, { value: 'minor', label: 'Light wear', factor: 0.95 }, { value: 'damaged', label: 'Dents or broken hinge', factor: 0.8 }] },
  { key: 'battery', label: 'Battery backup', options: [{ value: 'good', label: 'More than 3 hours', factor: 1 }, { value: 'fair', label: '1 – 3 hours', factor: 0.93 }, { value: 'poor', label: 'Under 1 hour / needs charger', factor: 0.85 }] },
  { key: 'charger', label: 'Original charger included?', options: [{ value: 'yes', label: 'Yes', factor: 1 }, { value: 'no', label: 'No', factor: 0.96 }] },
];

const pick = (list, value) => list.find((o) => o.value === value) || null;

/** Missing answers, in display order (empty array when the questionnaire is complete). */
export function missingAnswers(a) {
  const miss = [];
  if (!pick(SEGMENTS, a.segment)) miss.push('segment');
  if (!pick(AGES, a.age)) miss.push('age');
  for (const q of QUESTIONS) if (!pick(q.options, a[q.key])) miss.push(q.key);
  return miss;
}

/**
 * → { amount_paise, low_paise, high_paise, factors: [{ label, factor }] }
 * Rounded to the nearest ₹100; the range is ±8% for what inspection may find.
 */
export function estimateBuyback(a) {
  if (missingAnswers(a).length) return null;
  const seg = pick(SEGMENTS, a.segment);
  const age = pick(AGES, a.age);
  const factors = [{ label: age.label, factor: age.factor }];
  for (const q of QUESTIONS) {
    const o = pick(q.options, a[q.key]);
    if (o.factor !== 1) factors.push({ label: `${q.label} ${o.label}`, factor: o.factor });
  }
  const raw = factors.reduce((v, f) => v * f.factor, seg.base_paise);
  const round = (p) => Math.max(0, Math.round(p / 10000) * 10000);
  return { amount_paise: round(raw), low_paise: round(raw * 0.92), high_paise: round(raw * 1.08), factors };
}
